/**
 * The field day on the phone: start, route points, visits, close, with an offline queue.
 *
 * Everything the employee does is put in a queue first (kept in the phone's storage) and sent in
 * order. If there's no data connection it stays queued and is sent when the network returns; the
 * server accepts actions up to 12 hours late and ignores re-sends, so nothing is lost or doubled.
 * Lives outside React so tracking carries on while the person moves between screens.
 */
import type { Api } from "./api/api";
import { ApiError } from "./api/api";
import type { FieldMe, FieldPoint, Fix } from "./api/types";
import { isApp, onOnline, startTracking, type LocationError } from "./native";

type Item =
  | { id: string; kind: "start"; body: Fix & { client: "android-app" | "web" } }
  | { id: string; kind: "end"; body: Fix }
  | { id: string; kind: "visit"; body: Fix & { clientId: string; place: string; note?: string; photo?: string } }
  | { id: string; kind: "points"; body: FieldPoint[] };
export interface FieldState { queued: number; sending: boolean; tracking: boolean; lastPointAt: string | null; failed: { kind: string; message: string; code: string }[]; locationError: string | null }

const KEY = "kormi:fieldQueue";
const load = (): Item[] => { try { return JSON.parse(localStorage.getItem(KEY) ?? "[]"); } catch { return []; } };
const save = (q: Item[]) => { try { localStorage.setItem(KEY, JSON.stringify(q)); return true; } catch { return false; } };
const uid = () => (crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

let api: Api | null = null, queue: Item[] = load(), stopTrack: (() => Promise<void>) | null = null, timer: any = null, pending: FieldPoint[] = [];
let state: FieldState = { queued: queue.length, sending: false, tracking: false, lastPointAt: null, failed: [], locationError: null };
const subs = new Set<(s: FieldState) => void>();
const emit = (p: Partial<FieldState>) => { state = { ...state, ...p, queued: queue.length + (pending.length ? 1 : 0) }; subs.forEach((f) => f(state)); };
export const fieldState = { get: () => state, subscribe(f: (s: FieldState) => void) { subs.add(f); return () => { subs.delete(f); }; } };

export function initField(a: Api) { if (api) return; api = a; onOnline(() => flush()); timer = setInterval(() => { movePending(); flush(); }, 60_000); flush(); }

function push(i: Item) { queue.push(i); if (!save(queue)) emit({ failed: [...state.failed, { kind: i.kind, code: "STORAGE_FULL", message: "The phone's storage for unsent items is full. Connect to the internet to send them." }] }); emit({}); }
function movePending() { if (!pending.length) return; push({ id: uid(), kind: "points", body: pending.splice(0) }); }

/** Send queued items in order. Stops at the first network error and tries again later. */
export async function flush(): Promise<void> {
  if (!api || state.sending || !queue.length) return;
  emit({ sending: true });
  try {
    while (queue.length) {
      const i = queue[0];
      try {
        if (i.kind === "start") await api.field.start(i.body);
        else if (i.kind === "end") await api.field.end(i.body);
        else if (i.kind === "visit") await api.field.visit(i.body);
        else await api.field.points(i.body);
      } catch (e) {
        const err = e as ApiError;
        if (!(err instanceof ApiError) || err.code === "NETWORK" || err.status === 0 || err.status >= 500 || err.status === 401) break; // try later
        emit({ failed: [...state.failed, { kind: i.kind, code: err.code, message: err.message }] }); // the server refused it: report, don't retry forever
      }
      queue.shift(); save(queue); emit({});
    }
  } finally { emit({ sending: false }); }
}
export function clearFailed() { emit({ failed: [] }); }

export function queueStart(f: Fix) { push({ id: uid(), kind: "start", body: { ...f, at: f.at ?? new Date().toISOString(), client: isApp ? "android-app" : "web" } }); return flush(); }
export function queueEnd(f: Fix) { movePending(); push({ id: uid(), kind: "end", body: { ...f, at: f.at ?? new Date().toISOString() } }); return flush(); }
export function queueVisit(v: Fix & { place: string; note?: string; photo?: string }) { movePending(); push({ id: uid(), kind: "visit", body: { ...v, at: v.at ?? new Date().toISOString(), clientId: uid() } }); return flush(); }

/** Start or stop sending the route. Points are batched (every 5 points or a minute). */
export async function setTracking(on: boolean, me?: FieldMe, text?: { title: string; message: string }) {
  if (!on) { if (stopTrack) { await stopTrack(); stopTrack = null; } movePending(); emit({ tracking: false }); return; }
  if (stopTrack || !me || me.mode !== "TRACKING") return;
  try {
    stopTrack = await startTracking({ intervalSec: me.settings.intervalSec, title: text?.title ?? "Kormi HR", message: text?.message ?? "",
      onPoint: (p) => { pending.push(p); emit({ lastPointAt: p.at, locationError: null }); if (pending.length >= 5) { movePending(); flush(); } },
      onError: (e: LocationError) => emit({ locationError: e.code }) });
    emit({ tracking: true });
  } catch {
    // The phone couldn't start location updates (service blocked, plugin missing): the day stays
    // open and start/close punches still work; the page shows the location problem.
    stopTrack = null; emit({ tracking: false, locationError: "UNAVAILABLE" });
  }
}
export const _testing = { reset() { queue = []; pending = []; save(queue); clearInterval(timer); api = null; state = { ...state, failed: [], queued: 0 }; } };
